import { useState } from "react";
import {
  DragState,
  ResizeState,
  TextEditingState,
  TextResizeState,
  LogoEditingState,
  // WatermarkEditingState,
} from "../types/overlayTypes";

export interface TextRotationState {
  isRotating: boolean;
  textOverlayId?: string;
  startRotation: number;
  startX: number;
  startY: number;
  centerX: number;
  centerY: number;
}

export interface InlineTextEditingState {
  isEditing: boolean;
  textOverlayId?: string;
  text: string;
  position: { x: number; y: number };
  fontSize: number;
  fontColor: string;
  backgroundColor: string;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  padding: number;
  rotation: number;
  opacity: number;
  fontFamily?: string;
}

export const useOverlayState = () => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    element: null,
    textOverlayId: undefined,
    logoOverlayId: undefined,
    startX: 0,
    startY: 0,
    startPosition: { x: 0, y: 0 },
  });

  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    element: null,
    logoOverlayId: undefined,
    startX: 0,
    startY: 0,
    startSize: { width: 0, height: 0 },
    corner: null,
  });

  const [textEditingState, setTextEditingState] = useState<TextEditingState>({
    isEditing: false,
    textOverlayId: undefined,
    text: "",
    fontSize: 24,
    fontColor: "#ffffff",
    backgroundColor: "#000000",
    position: { x: 0, y: 0 },
    isBold: false,
    isItalic: false,
    isUnderline: false,
    padding: 0,
    rotation: 0,
    opacity: 1.0,
  });

  const [inlineTextEditingState, setInlineTextEditingState] =
    useState<InlineTextEditingState>({
      isEditing: false,
      textOverlayId: undefined,
      text: "",
      position: { x: 0, y: 0 },
      fontSize: 24,
      fontColor: "#ffffff",
      backgroundColor: "#000000",
      isBold: false,
      isItalic: false,
      isUnderline: false,
      padding: 0,
      rotation: 0,
      opacity: 1.0,
      fontFamily: "Arial",
    });

  const [textResizeState, setTextResizeState] = useState<TextResizeState>({
    isResizing: false,
    textOverlayId: undefined,
    startFontSize: 0,
    startX: 0,
    startY: 0,
    corner: null,
  });

  const [textRotationState, setTextRotationState] = useState<TextRotationState>(
    {
      isRotating: false,
      textOverlayId: undefined,
      startRotation: 0,
      startX: 0,
      startY: 0,
      centerX: 0,
      centerY: 0,
    }
  );

  const [showTextModal, setShowTextModal] = useState(false);

  const [logoEditingState, setLogoEditingState] = useState<LogoEditingState>({
    isEditing: false,
    logoOverlayId: undefined,
    url: "",
    position: { x: 0, y: 0 },
    size: { width: 200, height: 200 },
    opacity: 1.0,
    aspectRatio: 1.0,
    rotation: 0,
  });

  const [showLogoModal, setShowLogoModal] = useState(false);

  const resetDragState = () => {
    setDragState({
      isDragging: false,
      element: null,
      textOverlayId: undefined,
      logoOverlayId: undefined,
      startX: 0,
      startY: 0,
      startPosition: { x: 0, y: 0 },
    });
  };

  const resetResizeState = () => {
    setResizeState({
      isResizing: false,
      element: null,
      logoOverlayId: undefined,
      startX: 0,
      startY: 0,
      startSize: { width: 0, height: 0 },
      corner: null,
    });
  };

  const resetTextResizeState = () => {
    setTextResizeState({
      isResizing: false,
      textOverlayId: undefined,
      startFontSize: 0,
      startX: 0,
      startY: 0,
      corner: null,
    });
  };

  const resetTextRotationState = () => {
    setTextRotationState({
      isRotating: false,
      textOverlayId: undefined,
      startRotation: 0,
      startX: 0,
      startY: 0,
      centerX: 0,
      centerY: 0,
    });
  };

  const resetTextEditingState = () => {
    setTextEditingState({
      isEditing: false,
      textOverlayId: undefined,
      text: "",
      fontSize: 24,
      fontColor: "#ffffff",
      backgroundColor: "#000000",
      position: { x: 0, y: 0 },
      isBold: false,
      isItalic: false,
      isUnderline: false,
      padding: 0,
      rotation: 0,
      opacity: 1.0,
    });
  };

  const resetInlineTextEditingState = () => {
    setInlineTextEditingState({
      isEditing: false,
      textOverlayId: undefined,
      text: "",
      position: { x: 0, y: 0 },
      fontSize: 24,
      fontColor: "#ffffff",
      backgroundColor: "#000000",
      isBold: false,
      isItalic: false,
      isUnderline: false,
      padding: 0,
      rotation: 0,
      opacity: 1.0,
      fontFamily: "Arial",
    });
  };

  const resetLogoEditingState = () => {
    setLogoEditingState((prev) => ({
      isEditing: false,
      logoOverlayId: undefined,
      url: "",
      position: { x: 0, y: 0 },
      size: { width: 200, height: 200 },
      opacity: prev.opacity, // Preserve current opacity instead of resetting to 1.0
      aspectRatio: 1.0,
      rotation: prev.rotation || 0, // Preserve current rotation
    }));
  };

  const resetAllStates = () => {
    resetDragState();
    resetResizeState();
    resetTextResizeState();
    resetTextEditingState();
    resetInlineTextEditingState();
    resetLogoEditingState();
    resetTextRotationState();
  };

  return {
    dragState,
    setDragState,
    resizeState,
    setResizeState,
    textEditingState,
    setTextEditingState,
    inlineTextEditingState,
    setInlineTextEditingState,
    textResizeState,
    setTextResizeState,
    textRotationState,
    setTextRotationState,
    showTextModal,
    setShowTextModal,
    logoEditingState,
    setLogoEditingState,
    showLogoModal,
    setShowLogoModal,
    resetDragState,
    resetResizeState,
    resetTextResizeState,
    resetTextRotationState,
    resetTextEditingState,
    resetInlineTextEditingState,
    resetLogoEditingState,
    resetAllStates,
  };
};
